import { ReactElement, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { Avatar, Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { colors } from "../../common/colors";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import Separator from "../../components/Separator";
import SubmitButton from '../../components/SubmitButton';
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import appServices from "../../services/app-services/appServices";
import { convertUserImage } from "../../util/utils";

function GroupCancelMembership(): ReactElement {
  const defaultImage = require("../../assets/images/default.png");
  const navigate = useNavigate();

  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { user } = useSelector((state: any) => state.userAuth);
  const targetUserId = useLocation().state; // これが無ければ自分自身を退会させる
  const [kickMemberGroupUser, setKickMemberGroupUser] = useState<any>();
  const [groupIcon, setGroupIcon] = useState<File>();
  const [errorMessage, setErrorMessage] = useState<string[]>([]);

  const groupCancelErrorMessage: string[] =
    kickMemberGroupUser ? [
      T('group_cancel_membership_error_1'),
      '　',
      T('group_cancel_membership_error_2'),
      T('group_cancel_membership_error_3'),
      T('group_cancel_membership_error_4')
    ] : [
      T('group_cancel_membership_onself_error_1'),
      '　',
      T('group_cancel_membership_onself_error_2'),
      T('group_cancel_membership_onself_error_3'),
      T('group_cancel_membership_onself_error_4')
    ]

  useEffect(() => {
    if (targetUserId) {
      appServices.findMember(targetUserId, groupInfo?.id).then((res: any) => {
        setKickMemberGroupUser(res.member_group_user)
      })
    }
  }, [])

  useEffect(() => {
    appServices.getGroupIcon(groupInfo.id).then(blob => {
      const file = new File([blob], groupInfo.group_icon, { type: blob.type });
      setGroupIcon(file);
    }).catch(() => {
      console.error("Failed to get group icon image")
    });
  }, [groupInfo])
  // 退会処理
  const [isShowOkModal, setIsShowOkModal] = useState<boolean>(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);
  const handleSubmit = async () => {
    try {
      if (targetUserId) {
        await appServices.deleteMember(kickMemberGroupUser.id);
      } else {
        const res = await appServices.findMember(user.id, groupInfo?.id);
        await appServices.deleteMember(res?.member_group_user?.id)
      }
      setIsShowOkModal(true)
      return true
    } catch (error: any) {
      console.error(error)
      const errorKey = error?.response?.data?.error_key
      const responseMessage = error?.response?.data?.message ?? [T("api_unknown_error")]
      setErrorMessage(errorKey === "cannot_delete" ? groupCancelErrorMessage : responseMessage)
      setIsShowErrorModal(true)
      return false
    }
  }

  // 退会完了
  const close = () => navigate("/myfarm")

  // チェックボックス
  const [checkedItems, setCheckedItems] = useState([false, false, false, false])
  const handleCheckbox = (i: number) => {
    const items = [ ... checkedItems ]
    items[i] = !items[i]
    setCheckedItems(items)
  }

  // 文言取得
  const translate = (suffix: string) => T(`${kickMemberGroupUser ? 'group_cancel_membership' : 'group_cancel_membership_onself'}_${suffix}`)

  // 描画
  return (
    <div style={{ paddingTop: 100 }}>
      <GCBackButton onClick={() => navigate(-1)} />
      <Box component="div" style={style.topContainer}>
        { kickMemberGroupUser ?
          (
            <>
              <Avatar src={convertUserImage(kickMemberGroupUser.user.image)} alt={kickMemberGroupUser.user.nickname} sx={style.avatar} />
              {kickMemberGroupUser.user.nickname}
            </>
          ) : (
            <>
              <img alt="" height={26} width={26} style={{ borderRadius: 13, marginRight: 10 }}
                src={groupIcon instanceof File ? URL.createObjectURL(groupIcon) : defaultImage}
              />
              &nbsp;  {groupInfo?.groupName}
            </>
          )
        }
      </Box>
      <Separator sx={{ marginTop: "10px" }} color="gray" />
      <Box component="div" sx={style.container}>
        <Typography style={style.description}>
          {translate('description')}
        </Typography>
        {
          checkedItems.map((item: boolean, i: number) => (
            <FormControlLabel
              control={<Checkbox checked={item} onChange={(e) => handleCheckbox(i)} />}
              sx={style.checkboxController}
              label={translate(`checkbox_${i + 1}`)}
              key={i}
            />
          ))
        }
        <SubmitButton
          disabled={!Object.values(checkedItems).every((v) => v)}
          color="secondary"
          sx={style.submitButton}
          size={"large"}
          variant="contained"
          validate={async () => { return Object.values(checkedItems).every((v) => v) }}
          onSubmit={handleSubmit}
        >
          {translate('submit')}
        </SubmitButton>
      </Box>

      {/* 退会成功 */}
      <MessageModal
        open={isShowOkModal}
        onClose={() => close()}
        onContinue={() => close()}
        message={translate('modal_description')}
        showButton
        buttonStyles={style.dialogButton}
        buttonText={T("close")}
      />

      {/* 退会失敗 */}
      <ErrorMessageModal
        open={isShowErrorModal}
        messages={errorMessage}
        onClose={() => setIsShowErrorModal(false)}
        alignLeft={errorMessage.length > 1}
      />
    </div>
  )
}

export default GroupCancelMembership

const style = {
  topContainer: {
    display: "flex",
    marginTop: 10,
    padding: "0 10px",
    fontWeight: "700"
  },
  avatar: {
    height: 26,
    width: 26,
    marginRight: "10px"
  },
  container: {
    padding: "6px",
    textAlign: "center"
  },
  description: {
    fontWeight: "700",
    margin: "15px 15px 45px 15px"
  },
  checkboxController: {
    display: "flex",
    alignItems: "flex-start",
    "& .MuiCheckbox-root": {
      paddingTop: 0
    },
    "& .MuiFormControlLabel-label": {
      textAlign: "left",
      paddingBottom: "20px"
    }
  },
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "50px auto 10px auto",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
    marginBottom:8
  },
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "85%",
    maxWidth: "320px",
    padding: "50px 10px 20px 10px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "10px",
    transition: "opacity 0.3s ease-in-out",
    textAlign: "center"
  },
  modalButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: "30px"
  },
  dialogButton: {
    width: "50%",
    marginTop: "30px",
    minWitdh: "100px",
    alignSelf: "center",
    display: "inline-block",
    border: "3px solid",
    borderRadius: "10px",
    borderColor: colors.farm_blue200,
    color: colors.farm_black
  }
};
